import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Shirt, Eye, Send, CheckCircle2, Truck, Trash2, PackageCheck, Printer, Settings, AlertTriangle, Info, FileText, DatabaseZap, Pencil } from "lucide-react";
import { toast } from "sonner";
import {
  api,
  formatApiError,
  fmtDate,
  fmtIDR,
  fmtDateShort,
  openPdf,
  wibDateISO,
  wibDaysAgo,
} from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, Panel, StatusBadge } from "../../lib/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import {
  filterProductionOrders,
  ProductionTableControls,
  ProductionTablePagination,
} from "../../components/production/ProductionTableControls";

const ORDER_STATUSES = [
  "Draft",
  "Menunggu Approval",
  "Siap Dikirim",
  "Dikirim ke Konveksi",
  "Sedang Dikerjakan",
  "Diterima Sebagian",
  "Selesai",
  "Selesai dengan Selisih",
  "Ditutup dengan Selisih",
  "Dibatalkan",
];
const PAGE_SIZE = 10;

export default function ProductionOrders() {
  const { can, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(searchParams.get("detail"));
  const [reportOid, setReportOid] = useState(null);
  const [kopOpen, setKopOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(wibDaysAgo(30));
  const [dateTo, setDateTo] = useState(wibDateISO());
  const [page, setPage] = useState(1);
  const write = can("production_admin");
  const canApprove = can("production_spv", "supervisor");
  const load = () => api.get("/production/orders").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const filteredList = useMemo(() => filterProductionOrders({
    orders: list,
    search,
    status: statusFilter,
    dateFrom,
    dateTo,
  }), [list, search, statusFilter, dateFrom, dateTo]);
  const totalPages = Math.max(1, Math.ceil(filteredList.length / PAGE_SIZE));
  const pageItems = filteredList.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const resetFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setDateFrom(wibDaysAgo(30));
    setDateTo(wibDateISO());
    setPage(1);
  };
  const updatePage = (callback) => (value) => {
    callback(value);
    setPage(1);
  };
  const openDetail = (id) => {
    setDetail(id);
    setSearchParams({ detail: id });
  };
  const closeDetail = () => {
    setDetail(null);
    setSearchParams({});
  };
  return (
    <div data-testid="orders-page">
      <PageHeader title="Order Jahit / Konveksi" subtitle="Kirim potongan & aksesori ke konveksi, pantau outstanding">
        <div className="flex gap-2">
          {write && <Button variant="outline" onClick={() => setKopOpen(true)} data-testid="kop-spk-btn" className="border-taupe text-cocoa"><Settings className="w-4 h-4 mr-1.5" />Kop SPK</Button>}
          {write && <Button onClick={() => setOpen(true)} data-testid="add-order-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory"><Plus className="w-4 h-4 mr-1.5" /> Order Jahit</Button>}
        </div>
      </PageHeader>
      <CompanyProfileDialog open={kopOpen} setOpen={setKopOpen} />
      <ProductionTableControls
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={updatePage(setDateFrom)}
        onDateToChange={updatePage(setDateTo)}
        onReset={resetFilters}
        onSearchChange={updatePage(setSearch)}
        onStatusChange={updatePage(setStatusFilter)}
        resultCount={filteredList.length}
        search={search}
        status={statusFilter}
        statusOptions={ORDER_STATUSES}
        testIdPrefix="orders"
        totalCount={list.length}
      />
      <Panel>
        <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>No Order</TableHead><TableHead>Konveksi</TableHead><TableHead>Produk</TableHead>
            <TableHead className="text-right">Kirim</TableHead><TableHead className="text-right">Diterima</TableHead>
            <TableHead className="text-right">Est. Biaya</TableHead><TableHead>Target</TableHead><TableHead>Created At</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {pageItems.map((o) => (
              <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                <TableCell className="font-mono text-xs">{o.number}</TableCell>
                <TableCell>{o.partner_name}</TableCell>
                <TableCell className="font-medium text-cocoa" data-testid={`order-products-${o.id}`}>
                  {(o.product_names || [o.product_name]).map((productName) => (
                    <div key={productName}>{productName}</div>
                  ))}
                </TableCell>
                <TableCell className="text-right">{o.total_send}</TableCell>
                <TableCell className="text-right">{o.total_received || 0}</TableCell>
                <TableCell className="text-right">{fmtIDR(o.cost_estimate)}</TableCell>
                <TableCell>{fmtDateShort(o.target_date)}</TableCell>
                <TableCell data-testid={`order-created-at-${o.id}`}>{fmtDate(o.created_at)}</TableCell>
                <TableCell><StatusBadge status={o.status} /></TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => setReportOid(o.id)} data-testid={`order-report-${o.id}`} title="Laporan Penerimaan"><FileText className="w-4 h-4 text-cocoa" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => openDetail(o.id)} data-testid={`order-view-${o.id}`}><Eye className="w-4 h-4 text-cocoa" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {!pageItems.length && <TableRow><TableCell colSpan={10} className="text-center text-wmuted py-10"><Shirt className="w-6 h-6 mx-auto mb-2 opacity-50" />Tidak ada order yang sesuai filter.</TableCell></TableRow>}
          </TableBody>
        </Table>
        </div>
      </Panel>
      <ProductionTablePagination
        onPageChange={setPage}
        page={page}
        testIdPrefix="orders"
        totalPages={totalPages}
      />
      <OrderDialog open={open} setOpen={setOpen} onDone={load} />
      <OrderDetail oid={detail} setOid={closeDetail} onDone={load} write={write} isOwner={user?.role === "owner"} canApprove={canApprove} />
      <ReceivingReportDialog oid={reportOid} setOid={setReportOid} />
    </div>
  );
}

function ReceivingReportDialog({ oid, setOid }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!oid) { setData(null); return; }
    setLoading(true);
    api.get(`/production/orders/${oid}/receiving-report`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [oid]);

  const exportCsv = () => {
    if (!data) return;
    const head = ["Nama Varian", "HPP Bahan Baku", "Cutting Cost", "Production Cost", "HPP Total", "Diterima (ACT)", "Target (EST)", "Belum Diterima"];
    const body = data.rows.map((r) => [r.name, r.hpp_material, r.cutting_cost, r.production_cost, r.hpp_total, r.act, r.est, r.outstanding]);
    const csv = [head, ...body].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `laporan-penerimaan-${(data.order.number || oid)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const s = data?.summary;
  const cards = s ? [
    { lbl: "Target (EST)", val: s.total_est, tid: "report-total-est" },
    { lbl: "Diterima (ACT)", val: s.total_act, tid: "report-total-act" },
    { lbl: "Belum Diterima", val: s.total_outstanding, tid: "report-total-outstanding" },
    { lbl: "Progres", val: `${s.progress_pct}%`, tid: "report-progress" },
  ] : [];

  return (
    <Dialog open={!!oid} onOpenChange={(o) => !o && setOid(null)}>
      <DialogContent className="max-w-5xl" data-testid="receiving-report-dialog">
        <DialogHeader>
          <DialogTitle>Laporan Penerimaan Produksi</DialogTitle>
          <DialogDescription>
            {data ? `${data.order.number || "-"} · ${(data.order.product_names || []).join(", ")} · ${data.order.partner_name || "-"}` : "Memuat resume penerimaan produksi..."}
          </DialogDescription>
        </DialogHeader>
        {loading && <div className="py-10 text-center text-wmuted">Memuat laporan...</div>}
        {data && !loading && (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {cards.map((c) => (
                <div className="rounded-lg border border-taupe/30 bg-taupe/5 p-3" key={c.tid}>
                  <div className="text-xs text-wmuted">{c.lbl}</div>
                  <div className="text-lg font-semibold text-cocoa" data-testid={c.tid}>{c.val}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-taupe/20">
              <div className="h-full bg-status-passed" style={{ width: `${Math.min(100, s.progress_pct)}%` }} />
            </div>
            <div className="mt-3 max-h-[26rem] overflow-auto">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent">
                  <TableHead>Nama Varian</TableHead>
                  <TableHead className="text-right">HPP Bahan Baku</TableHead>
                  <TableHead className="text-right">Cutting</TableHead>
                  <TableHead className="text-right">Production</TableHead>
                  <TableHead className="text-right">HPP Total</TableHead>
                  <TableHead className="text-right">Qty (ACT/EST)</TableHead>
                  <TableHead className="text-right">Belum</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.rows.map((r, i) => (
                    <TableRow key={r.variant_id || i} data-testid={`report-row-${i}`}>
                      <TableCell className="font-medium text-cocoa">
                        {r.name}
                        {!r.bom_complete && <span className="ml-1 text-xs text-amber-600" title="Resep BOM belum lengkap">(BOM?)</span>}
                      </TableCell>
                      <TableCell className="text-right">{fmtIDR(r.hpp_material)}</TableCell>
                      <TableCell className="text-right">{fmtIDR(r.cutting_cost)}</TableCell>
                      <TableCell className="text-right">{fmtIDR(r.production_cost)}</TableCell>
                      <TableCell className="text-right font-semibold">{fmtIDR(r.hpp_total)}</TableCell>
                      <TableCell className={`text-right font-semibold ${r.act >= r.est ? "text-status-passed" : "text-rose-500"}`} data-testid={`report-actest-${i}`}>{r.act} / {r.est}</TableCell>
                      <TableCell className="text-right">{r.outstanding}</TableCell>
                    </TableRow>
                  ))}
                  {!data.rows.length && <TableRow><TableCell className="py-8 text-center text-wmuted" colSpan={7}>Belum ada data varian.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </>
        )}
        <DialogFooter>
          <Button onClick={() => setOid(null)} variant="outline">Tutup</Button>
          <Button className="bg-cocoa text-ivory hover:bg-cocoa-dark" data-testid="report-export-btn" disabled={!data} onClick={exportCsv}><FileText className="w-4 h-4 mr-1.5" />Export CSV</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OrderDialog({ open, setOpen, onDone }) {
  const [cuts, setCuts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [accInfo, setAccInfo] = useState(null);
  const [whs, setWhs] = useState([]);
  const [step, setStep] = useState(1);
  const [bulkSew, setBulkSew] = useState("");
  const [costRows, setCostRows] = useState([]);  // per-variant costing
  const [recipeNeeded, setRecipeNeeded] = useState(false);
  const [recipeRows, setRecipeRows] = useState([]);
  const [prodId, setProdId] = useState("");
  const [recipeOpen, setRecipeOpen] = useState(false);
  const blank = { cutting_order_id: "", partner_id: "", send_date: "", target_date: "", warehouse_id: "", extra_cost: 0, pic: "", note: "", details: [], accessories: [] };
  const [form, setForm] = useState(blank);
  useEffect(() => { if (open) { setStep(1); setBulkSew(""); setCostRows([]); setAccInfo(null); setForm(blank);
    api.get("/production/cutting").then((r) => setCuts(r.data.filter((c) => (c.output_total || 0) - (c.sent_qty || 0) > 0)));
    api.get("/suppliers", { params: { active: "active", partner_type: "konveksi" } }).then((r) => setPartners(r.data));
    api.get("/production/accessories", { params: { active: "active" } }).then((r) => setAccessories(r.data));
    api.get("/warehouses", { params: { usage: "produk_jadi" } }).then((r) => { setWhs(r.data); }); } /* eslint-disable-next-line */ }, [open]);
  const cut = cuts.find((c) => c.id === form.cutting_order_id);
  const totalSent = form.details.reduce((s, d) => s + (parseInt(d.qty) || 0), 0);
  useEffect(() => {
    if (!cut) return;
    setForm((f) => ({ ...f, plan_id: cut.plan_id, details: cut.outputs.map((o) => ({ size: o.size, qty: o.qty })) }));
    api.get("/production/costing-preview", { params: { cutting_order_id: form.cutting_order_id } })
      .then((r) => { setProdId(r.data.product_id || ""); setRecipeNeeded(!r.data.has_accessory_recipe); setRecipeRows((r.data.accessory_recipe || []).map((a) => ({ material_id: a.material_id, usage_per_pcs: a.usage_per_pcs || "" }))); })
      .catch(() => {});
    /* eslint-disable-next-line */
  }, [form.cutting_order_id]);
  useEffect(() => {
    if (!form.cutting_order_id || totalSent <= 0) return;
    api.get("/production/orders/suggest-accessories", { params: { cutting_order_id: form.cutting_order_id, qty: totalSent } })
      .then((r) => { setAccInfo(r.data); setForm((f) => ({ ...f, accessories: (r.data.items || []).map((it) => ({ material_id: it.material_id, name: it.name, unit: "pcs", pack_size: 1, qty: it.recommended })) })); })
      .catch(() => setAccInfo(null));
    /* eslint-disable-next-line */
  }, [form.cutting_order_id, totalSent]);
  const setDet = (i, v) => setForm((f) => ({ ...f, details: f.details.map((d, idx) => idx === i ? { ...d, qty: v } : d) }));
  const addAcc = () => setForm((f) => ({ ...f, accessories: [...f.accessories, { material_id: "", name: "", unit: "pcs", qty: 0 }] }));
  const setAcc = (i, patch) => setForm((f) => ({ ...f, accessories: f.accessories.map((a, idx) => idx === i ? { ...a, ...patch } : a) }));

  const loadCosting = async () => {
    try {
      const r = await api.get("/production/costing-preview", { params: { cutting_order_id: form.cutting_order_id } });
      const qtyBySize = {}; form.details.forEach((d) => { qtyBySize[d.size] = parseInt(d.qty) || 0; });
      setCostRows((r.data.rows || []).map((row) => ({ ...row, qty: qtyBySize[row.size] ?? row.qty, sewing_cost: "" })));
      setProdId(r.data.product_id || "");
      setRecipeNeeded(!r.data.has_accessory_recipe);
      setRecipeRows((r.data.accessory_recipe || []).map((a) => ({ material_id: a.material_id, usage_per_pcs: a.usage_per_pcs || "" })));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const refreshRecipe = () => {
    if (!form.cutting_order_id) return;
    api.get("/production/costing-preview", { params: { cutting_order_id: form.cutting_order_id } })
      .then((r) => { setProdId(r.data.product_id || ""); setRecipeNeeded(!r.data.has_accessory_recipe); setRecipeRows((r.data.accessory_recipe || []).map((a) => ({ material_id: a.material_id, usage_per_pcs: a.usage_per_pcs || "" }))); })
      .catch(() => {});
    if (totalSent > 0) api.get("/production/orders/suggest-accessories", { params: { cutting_order_id: form.cutting_order_id, qty: totalSent } })
      .then((r) => { setAccInfo(r.data); setForm((f) => ({ ...f, accessories: (r.data.items || []).map((it) => ({ material_id: it.material_id, name: it.name, unit: "pcs", pack_size: 1, qty: it.recommended })) })); })
      .catch(() => {});
  };
  useEffect(() => {
    const h = (e) => { if (e.data && e.data.type === "bom-saved") { refreshRecipe(); toast.success("Resep diperbarui dari Master Resep"); } };
    window.addEventListener("message", h);
    return () => window.removeEventListener("message", h);
    /* eslint-disable-next-line */
  }, [form.cutting_order_id, totalSent]);
  const recipeValid = recipeRows.length > 0 && recipeRows.every((r) => r.material_id && parseFloat(r.usage_per_pcs) > 0);
  const setRcp = (i, patch) => setRecipeRows((rs) => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const saveRecipe = async () => {
    if (!recipeValid) return toast.error("Isi kebutuhan/pcs (>0) untuk setiap aksesori");
    try {
      await api.post("/production/orders/save-accessory-recipe", { product_id: prodId, components: recipeRows.map((r) => ({ material_id: r.material_id, usage_per_pcs: parseFloat(r.usage_per_pcs) || 0 })) });
      toast.success("Resep aksesori disimpan ke produk");
      await loadCosting();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setSew = (i, v) => setCostRows((rs) => rs.map((r, idx) => idx === i ? { ...r, sewing_cost: v } : r));
  const applyBulkSew = () => { const v = parseFloat(bulkSew); if (!(v >= 0)) return; setCostRows((rs) => rs.map((r) => ({ ...r, sewing_cost: v }))); };
  const hpp = (r) => (parseFloat(r.material_cost) || 0) + (parseFloat(r.accessory_cost) || 0) + (parseFloat(r.cutting_cost) || 0) + (parseFloat(r.sewing_cost) || 0);
  const activeCost = costRows.filter((r) => (parseInt(r.qty) || 0) > 0);
  const totalHpp = activeCost.reduce((s, r) => s + hpp(r) * (parseInt(r.qty) || 0), 0);
  const allSewingFilled = activeCost.length > 0 && activeCost.every((r) => parseFloat(r.sewing_cost) > 0);

  const goStep2to3 = async () => {
    if (!form.details.some((d) => parseInt(d.qty) > 0)) return toast.error("Isi jumlah dikirim minimal satu varian");
    await loadCosting(); setStep(3);
  };

  const save = async () => {
    if (!allSewingFilled) return toast.error("Biaya jahit wajib > 0 untuk semua varian yang dikirim");
    const details = form.details.filter((d) => parseInt(d.qty) > 0).map((d) => ({ size: d.size, qty: parseInt(d.qty) }));
    const acc = form.accessories.filter((a) => a.material_id && parseFloat(a.qty) > 0).map((a) => ({ material_id: a.material_id, name: a.name, unit: a.unit, qty: parseFloat(a.qty), pack_size: a.pack_size || 1 }));
    const costings = activeCost.map((r) => ({ variant_id: r.variant_id, color: r.color, size: r.size, sewing_cost: parseFloat(r.sewing_cost) || 0 }));
    try {
      await api.post("/production/orders", { plan_id: cut.plan_id, cutting_order_id: form.cutting_order_id, partner_id: form.partner_id, details, accessories: acc, send_date: form.send_date, target_date: form.target_date, warehouse_id: form.warehouse_id || null, extra_cost: parseFloat(form.extra_cost) || 0, costings, pic: form.pic, note: form.note });
      toast.success("Sewing Order dibuat & HPP dikunci"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const STEPS = ["Order Details", "Variant Quantity", "Costing & Fixed HPP"];
  return (
    <>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Sewing Order Baru — {STEPS[step - 1]}</DialogTitle></DialogHeader>
        <div className="flex items-center gap-1 mb-3 text-xs">
          {STEPS.map((label, i) => { const n = i + 1; return (<React.Fragment key={n}>
            <div className={`flex items-center gap-1.5 ${step >= n ? "text-cocoa" : "text-wmuted"}`}><span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold ${step > n ? "bg-status-passed text-white" : step === n ? "bg-cocoa text-ivory" : "bg-taupe/20 text-wmuted"}`}>{step > n ? "✓" : n}</span><span className="font-medium hidden sm:inline">{label}</span></div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-1 ${step > n ? "bg-status-passed" : "bg-taupe/25"}`} />}
          </React.Fragment>); })}
        </div>

        {step === 1 && <div className="grid grid-cols-2 gap-3" data-testid="oj-step-1">
          <div className="col-span-2"><Label>Cutting Order *</Label>
            <Select value={form.cutting_order_id} onValueChange={(v) => setForm({ ...form, cutting_order_id: v })}><SelectTrigger data-testid="order-cutting"><SelectValue placeholder="Pilih hasil cutting" /></SelectTrigger><SelectContent>{cuts.map((c) => <SelectItem key={c.id} value={c.id}>{c.number} — {c.product_name} (sisa {(c.output_total || 0) - (c.sent_qty || 0)})</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="col-span-2"><Label>Konveksi / Tailor *</Label>
            <Select value={form.partner_id} onValueChange={(v) => setForm({ ...form, partner_id: v })}><SelectTrigger data-testid="order-partner"><SelectValue placeholder="Pilih konveksi" /></SelectTrigger><SelectContent>{partners.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Gudang Tujuan</Label>
            <Select value={form.warehouse_id || undefined} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}><SelectTrigger data-testid="order-warehouse"><SelectValue placeholder="Default (Home)" /></SelectTrigger><SelectContent>{whs.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>PIC</Label><Input value={form.pic} onChange={(e) => setForm({ ...form, pic: e.target.value })} /></div>
          <div><Label>Tanggal Kirim</Label><Input type="date" value={form.send_date} onChange={(e) => setForm({ ...form, send_date: e.target.value })} /></div>
          <div><Label>Target Selesai</Label><Input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} /></div>
          <div className="col-span-2"><div className="flex items-center justify-between"><Label>Aksesori Dikirim</Label>{prodId && <Button variant="ghost" size="sm" className="h-7 text-cocoa hover:bg-ivory" onClick={() => setRecipeOpen(true)} data-testid="oj-edit-resep"><FileText className="w-3.5 h-3.5 mr-1" />{accInfo?.has_recipe ? "Edit Resep" : "Buat Resep"}</Button>}</div>
            {accInfo && accInfo.has_recipe && form.accessories.length > 0 && <p className="text-[11px] text-status-passed mb-1" data-testid="acc-auto-hint">Otomatis dihitung dari <b>{accInfo.total_pcs} pcs</b> yang dikirim × kebutuhan/pcs (resep) + buffer minimal {accInfo.buffer_pct}%. Sesuaikan bila perlu.</p>}
            {accInfo && accInfo.has_recipe && form.accessories.length === 0 && <p className="text-[11px] text-wmuted mb-1">Resep produk ini tidak memuat aksesori — tambahkan manual bila ada yang perlu dikirim.</p>}
            {accInfo && !accInfo.has_recipe && <p className="text-[11px] text-wmuted mb-1">Produk belum punya resep aksesori — tambah manual atau buat dulu di Master Resep.</p>}
            {form.accessories.map((a, i) => { const macc = accessories.find((x) => x.id === a.material_id); const packOpts = [...new Set((macc?.packs || []).map((p) => Number(p.pcs_per_pack)).filter((n) => n > 0))]; const curVal = a.unit === "pack" ? `pack:${a.pack_size || packOpts[0] || 1}` : "pcs"; return (<div key={i} className="flex gap-2 mt-1 items-center"><Select value={a.material_id || "none"} onValueChange={(v) => { const m = accessories.find((x) => x.id === v); setAcc(i, { material_id: v === "none" ? "" : v, name: m?.name || "", unit: "pcs", pack_size: 1 }); }}><SelectTrigger className="flex-1"><SelectValue placeholder="Pilih aksesori" /></SelectTrigger><SelectContent><SelectItem value="none">- Pilih -</SelectItem>{accessories.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent></Select><Input type="number" placeholder="Qty" value={a.qty} onChange={(e) => setAcc(i, { qty: e.target.value })} className="w-20" /><Button size="icon" variant="ghost" onClick={() => setForm((f) => ({ ...f, accessories: f.accessories.filter((_, idx) => idx !== i) }))}><Trash2 className="w-4 h-4 text-status-rejected" /></Button></div>); })}
            <Button variant="outline" size="sm" className="mt-2 border-taupe text-cocoa" onClick={addAcc}><Plus className="w-3.5 h-3.5 mr-1" />Tambah Aksesori</Button>
          </div>
          {recipeNeeded && <div className="col-span-2 bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-center justify-between gap-2" data-testid="oj-recipe-builder-s1">
            <p className="text-sm text-amber-800 flex items-center gap-1.5"><Info className="w-4 h-4 shrink-0" />Resep produk ini belum lengkap (baru kain). Lengkapi aksesori/label/packaging/ongkos di Master Resep.</p>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap" onClick={() => setRecipeOpen(true)} disabled={!prodId} data-testid="oj-open-master-resep"><FileText className="w-4 h-4 mr-1" />Buka Master Resep</Button>
          </div>}
          <div className="col-span-2"><Label>Catatan</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} /></div>
        </div>}

        {step === 2 && <div data-testid="oj-step-2">
          <table className="w-full text-sm"><thead><tr className="text-left text-wmuted border-b border-taupe/20"><th className="py-2 px-2">Variant</th><th className="py-2 px-2 text-right">Qty Sent</th></tr></thead>
            <tbody>{form.details.map((d, i) => <tr key={i} className="border-b border-taupe/10"><td className="py-2 px-2">{d.size || "-"}</td><td className="py-2 px-2 text-right"><Input type="number" min="0" value={d.qty} onChange={(e) => setDet(i, e.target.value)} className="w-24 ml-auto text-right" data-testid={`order-det-${i}`} /></td></tr>)}</tbody>
          </table>
          <div className="mt-3 text-right text-sm">Total dikirim: <b className="text-cocoa" data-testid="oj-total-qty">{totalSent} pcs</b></div>
          <div className="mt-4 bg-ivory/60 border border-taupe/25 rounded-lg p-3" data-testid="oj-kiriman-recap">
            <p className="font-medium text-cocoa text-sm mb-2 flex items-center gap-1.5"><PackageCheck className="w-4 h-4" />Yang Dikirim ke Konveksi — checklist persiapan</p>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-[11px] text-wmuted uppercase tracking-wide mb-1">Potongan (per ukuran)</div>
                <ul className="space-y-0.5">{form.details.filter((d) => parseInt(d.qty) > 0).map((d, i) => <li key={i} className="flex justify-between"><span className="text-cocoa">{d.size || "-"}</span><span className="font-medium">{parseInt(d.qty)} pcs</span></li>)}</ul>
                <div className="flex justify-between border-t border-taupe/20 mt-1 pt-1 font-semibold text-cocoa"><span>Total Potongan</span><span>{totalSent} pcs</span></div>
              </div>
              <div>
                <div className="text-[11px] text-wmuted uppercase tracking-wide mb-1">Aksesori {accInfo?.has_recipe && <span className="text-status-passed normal-case">(otomatis dari resep, buffer {accInfo.buffer_pct}%)</span>}</div>
                {form.accessories.filter((a) => parseFloat(a.qty) > 0).length > 0
                  ? <ul className="space-y-0.5">{form.accessories.filter((a) => parseFloat(a.qty) > 0).map((a, i) => <li key={i} className="flex justify-between"><span className="text-cocoa">{a.name || "(aksesori)"}</span><span className="font-medium">{a.qty} {a.unit || "pcs"}</span></li>)}</ul>
                  : <p className="text-wmuted text-xs italic">Tidak ada aksesori pada resep. Tambahkan manual di langkah sebelumnya bila diperlukan.</p>}
              </div>
            </div>
          </div>
        </div>}

        {step === 3 && <div data-testid="oj-step-3">
          {recipeNeeded && <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-3" data-testid="oj-recipe-builder">
            <p className="text-sm font-medium text-amber-800 flex items-center gap-1.5 mb-2"><Info className="w-4 h-4" />Resep aksesori belum ada — isi dulu (wajib). Harga satuan otomatis dari master aksesori, akan disimpan ke resep produk.</p>
            {recipeRows.map((r, i) => (
              <div key={i} className="flex gap-2 mb-1 items-center">
                <Select value={r.material_id || "none"} onValueChange={(v) => setRcp(i, { material_id: v === "none" ? "" : v })}><SelectTrigger className="flex-1" data-testid={`oj-recipe-acc-${i}`}><SelectValue placeholder="Pilih aksesori" /></SelectTrigger><SelectContent><SelectItem value="none">- Pilih -</SelectItem>{accessories.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent></Select>
                <Input type="number" min="0" step="0.01" placeholder="Kebutuhan/pcs" value={r.usage_per_pcs} onChange={(e) => setRcp(i, { usage_per_pcs: e.target.value })} className="w-32" data-testid={`oj-recipe-usage-${i}`} />
                <span className="text-[11px] text-wmuted whitespace-nowrap">per 1 pcs</span>
                <Button size="icon" variant="ghost" onClick={() => setRecipeRows((rs) => rs.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4 text-status-rejected" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="mt-1 border-amber-400 text-amber-700" onClick={() => setRecipeRows((rs) => [...rs, { material_id: "", usage_per_pcs: "" }])} data-testid="oj-recipe-add"><Plus className="w-3.5 h-3.5 mr-1" />Tambah Aksesori Resep</Button>
          </div>}
          <div className="bg-ivory/50 border border-taupe/25 rounded-lg p-3 flex flex-wrap items-center gap-2 mb-3">
            <span className="text-sm font-medium text-cocoa">Biaya Jahit</span>
            <span className="text-xs text-wmuted">Rp</span><Input type="number" value={bulkSew} onChange={(e) => setBulkSew(e.target.value)} className="h-9 w-32" data-testid="oj-bulk-sew" />
            <Button size="sm" className="bg-cocoa text-ivory" onClick={applyBulkSew} data-testid="oj-apply-sew">Apply to All</Button>
            <span className="text-[11px] text-wmuted ml-auto">Kain & Aksesori dari BOM, Cutting dari cutting order — otomatis.</span>
          </div>
          <div className="overflow-x-auto border border-taupe/20 rounded-lg">
            <table className="w-full text-sm"><thead><tr className="text-left text-wmuted border-b border-taupe/20 bg-ivory/40"><th className="py-2 px-2">Variant</th><th className="py-2 px-2 text-right">Qty</th><th className="py-2 px-2 text-right">Kain</th><th className="py-2 px-2 text-right">Aksesori</th><th className="py-2 px-2 text-right">Cutting</th><th className="py-2 px-2 text-center">Biaya Jahit</th><th className="py-2 px-2 text-right">Fixed HPP</th></tr></thead>
              <tbody>{costRows.map((r, i) => (
                <tr key={i} className="border-b border-taupe/10" data-testid={`oj-cost-${i}`}>
                  <td className="py-2 px-2 text-cocoa font-medium">{r.color ? `${r.color} / ` : ""}{r.size}</td>
                  <td className="py-2 px-2 text-right">{r.qty}</td>
                  <td className="py-2 px-2 text-right">{fmtIDR(r.material_cost)}</td>
                  <td className="py-2 px-2 text-right">{fmtIDR(r.accessory_cost)}</td>
                  <td className="py-2 px-2 text-right">{fmtIDR(r.cutting_cost)}</td>
                  <td className="py-2 px-2"><div className="flex items-center gap-1 justify-center"><span className="text-xs text-wmuted">Rp</span><Input type="number" value={r.sewing_cost} onChange={(e) => setSew(i, e.target.value)} className="h-8 w-24 text-right" data-testid={`oj-sew-${i}`} /></div></td>
                  <td className="py-2 px-2 text-right font-bold text-cocoa" data-testid={`oj-hpp-${i}`}>{fmtIDR(hpp(r))}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          {!allSewingFilled && <p className="text-[11px] text-status-reject mt-2 flex items-center gap-1"><Info className="w-3.5 h-3.5" />Isi biaya jahit (&gt;0) untuk semua varian agar HPP bisa dikunci.</p>}
          <div className="mt-3 text-right text-sm">Total Estimated Production Cost: <b className="text-cocoa" data-testid="oj-total-hpp">{fmtIDR(totalHpp)}</b></div>
        </div>}

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => setOpen(false)} className="border-taupe text-cocoa">Batal</Button>
          {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} className="border-taupe text-cocoa" data-testid="oj-back">Kembali</Button>}
          {step === 1 && <Button onClick={() => { if (!form.cutting_order_id) return toast.error("Pilih cutting order"); if (!form.partner_id) return toast.error("Pilih konveksi"); setStep(2); }} className="bg-cocoa text-ivory" data-testid="oj-next-1">Lanjut</Button>}
          {step === 2 && <Button onClick={goStep2to3} className="bg-cocoa text-ivory" data-testid="oj-next-2">Lanjut Costing & HPP</Button>}
          {step === 3 && recipeNeeded && <Button onClick={saveRecipe} disabled={!recipeValid} data-testid="oj-save-recipe-btn" className="bg-amber-600 hover:bg-amber-700 text-white">Simpan Resep & Hitung HPP</Button>}
          {step === 3 && !recipeNeeded && <Button onClick={save} disabled={!allSewingFilled} data-testid="save-order-btn" className="bg-status-passed text-white">Simpan & Kunci HPP</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <Dialog open={recipeOpen} onOpenChange={(v) => { setRecipeOpen(v); if (!v) refreshRecipe(); }}>
      <DialogContent className="max-w-6xl w-[96vw] h-[90vh] p-0 overflow-hidden flex flex-col" data-testid="oj-master-resep-dialog">
        <DialogHeader className="px-4 py-2.5 border-b border-taupe/20 shrink-0"><DialogTitle className="font-heading text-cocoa text-base flex items-center gap-1.5"><FileText className="w-4 h-4" />Master Resep Produksi</DialogTitle></DialogHeader>
        {recipeOpen && prodId && <iframe title="Master Resep Produksi" src={`/production/bom?product=${prodId}&embed=1`} className="w-full flex-1 border-0" data-testid="oj-master-resep-iframe" />}
      </DialogContent>
    </Dialog>
    </>
  );
}

export function ReceiveDialog({ d, detColor, warehouses, onClose, onDone }) {
  const isMultiProduct = (d.product_lines || []).length > 1;
  const colors = d.variant_colors || [];
  const rows0 = (d.details || []).map((detail) => {
    const outstanding = Math.max(
      0,
      (Number(detail.qty) || 0) - (Number(detail.received_qty) || 0),
    );
    const mappedColor = detColor(detail);
    const color = detail.color || mappedColor || colors[0] || "";
    return {
      variant_id: detail.variant_id || "",
      product_id: detail.product_id || d.product_id,
      product_name: detail.product_name || d.product_name,
      size: detail.size,
      color,
      outstanding,
      received: outstanding,
      passed: outstanding,
      rework: 0,
      reject: 0,
    };
  });
  const [rows, setRows] = useState(rows0);
  const [qc, setQc] = useState(true);
  const [wh, setWh] = useState(warehouses[0]?.id || "");
  const set = (i, patch) => setRows(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const save = async () => {
    if (!wh) return toast.error("Pilih gudang");
    const lines = rows.filter((r) => parseInt(r.received) > 0).map((r) => ({
      variant_id: r.variant_id || null,
      product_id: r.product_id,
      product_name: r.product_name,
      size: r.size,
      color: r.color,
      received: parseInt(r.received) || 0,
      passed: qc ? (parseInt(r.passed) || 0) : (parseInt(r.received) || 0),
      rework: qc ? (parseInt(r.rework) || 0) : 0,
      reject: qc ? (parseInt(r.reject) || 0) : 0,
    }));
    if (!lines.length) return toast.error("Isi jumlah diterima");
    try { const res = await api.post(`/production/orders/${d.id}/receive`, { warehouse_id: wh, qc, lines }); toast.success(`Diterima → ${res.data.status} (Passed ${res.data.passed}, Rework ${res.data.rework}, Reject ${res.data.reject})`); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        aria-describedby="production-order-detail-description"
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-cocoa">
            Terima Produksi & QC — {d.number}
          </DialogTitle>
          <DialogDescription className="sr-only" data-testid="receive-dialog-description">
            Masukkan jumlah penerimaan dan hasil quality control setiap varian.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-xs text-wmuted">Gudang masuk</span>
            <Select value={wh} onValueChange={setWh}><SelectTrigger className="w-52" data-testid="receive-wh"><SelectValue placeholder="Pilih gudang" /></SelectTrigger><SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent></Select>
            <label className="flex items-center gap-1.5 text-sm ml-auto"><input type="checkbox" checked={qc} onChange={(e) => setQc(e.target.checked)} className="accent-cocoa" data-testid="receive-qc-toggle" />Lakukan QC</label>
          </div>
          <p className="text-[11px] text-wmuted">Semua qty diterima dicatat ke stok <b>terpisah per status</b>: Passed → stok jual (available), Rework & Reject → tercatat terpisah (tidak dijual). Bila QC dimatikan, semua dianggap Passed.</p>
          <Panel>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {isMultiProduct && <TableHead>Produk</TableHead>}
                  <TableHead>Varian</TableHead>
                  <TableHead className="text-right">Sisa</TableHead>
                  <TableHead className="text-right">Diterima</TableHead>
                  {qc && <>
                    <TableHead className="text-right">Passed</TableHead>
                    <TableHead className="text-right">Rework</TableHead>
                    <TableHead className="text-right">Reject</TableHead>
                  </>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={`${r.product_id}-${r.variant_id || i}`} data-testid={`receive-row-${i}`}>
                    {isMultiProduct && (
                      <TableCell className="text-sm text-cocoa" data-testid={`receive-product-${i}`}>
                        {r.product_name}
                      </TableCell>
                    )}
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-1.5">
                        {!isMultiProduct && colors.length > 0 ? (
                          <Select
                            value={r.color || undefined}
                            onValueChange={(value) => set(i, { color: value })}
                          >
                            <SelectTrigger className="w-28 h-8" data-testid={`receive-color-${i}`}>
                              <SelectValue placeholder="Warna" />
                            </SelectTrigger>
                            <SelectContent>
                              {colors.map((color) => (
                                <SelectItem key={color} value={color}>{color}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (r.color ? <span>{r.color}</span> : null)}
                        <span className="text-wmuted">{r.size}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-xs text-wmuted">{r.outstanding}</TableCell>
                    <TableCell className="text-right"><Input type="number" value={r.received} onChange={(e) => set(i, { received: e.target.value, passed: qc ? e.target.value : r.passed })} className="w-20 ml-auto" data-testid={`receive-received-${i}`} /></TableCell>
                    {qc && <>
                      <TableCell className="text-right"><Input type="number" value={r.passed} onChange={(e) => set(i, { passed: e.target.value })} className="w-16 ml-auto" data-testid={`receive-passed-${i}`} /></TableCell>
                      <TableCell className="text-right"><Input type="number" value={r.rework} onChange={(e) => set(i, { rework: e.target.value })} className="w-16 ml-auto" data-testid={`receive-rework-${i}`} /></TableCell>
                      <TableCell className="text-right"><Input type="number" value={r.reject} onChange={(e) => set(i, { reject: e.target.value })} className="w-16 ml-auto" data-testid={`receive-reject-${i}`} /></TableCell>
                    </>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Panel>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">Batal</Button><Button onClick={save} data-testid="receive-save-btn" className="bg-status-passed text-white">Terima & Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OrderDetail({ oid, setOid, onDone, write, isOwner, canApprove }) {
  const [d, setD] = useState(null);
  const [sendWhs, setSendWhs] = useState([]);
  const [recvWhs, setRecvWhs] = useState([]);
  const [wh, setWh] = useState("");
  const [recv, setRecv] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [accChk, setAccChk] = useState(null);
  const [cutCost, setCutCost] = useState("");
  const [savingCut, setSavingCut] = useState(false);
  const [sewCost, setSewCost] = useState("");
  const [savingSew, setSavingSew] = useState(false);
  const load = () => oid && api.get(`/production/orders/${oid}`).then((r) => { setD(r.data); api.get(`/production/orders/${oid}/accessory-check`).then((rr) => setAccChk(rr.data)).catch(() => setAccChk(null)); });
  useEffect(() => { setD(null); load(); api.get("/warehouses", { params: { usage: "material" } }).then((r) => { setSendWhs(r.data); setWh(r.data[0]?.id || ""); }); api.get("/warehouses", { params: { usage: "produk_jadi" } }).then((r) => setRecvWhs(r.data)); /* eslint-disable-next-line */ }, [oid]);
  useEffect(() => {
    if (d?.hpp) {
      const cutLine = (d.hpp.lines || []).find((l) => l.label === "Ongkos Cutting");
      setCutCost(String(cutLine?.per_pcs ?? 0));
      const sewLine = (d.hpp.lines || []).find((l) => l.label === "Ongkos Jahit");
      setSewCost(String(sewLine?.per_pcs ?? 0));
    }
  }, [d]);
  if (!oid) return null;
  const saveCutCost = async () => {
    const v = parseFloat(cutCost);
    if (Number.isNaN(v) || v < 0) return toast.error("Biaya cutting tidak valid");
    setSavingCut(true);
    try {
      await api.put(`/production/orders/${oid}/cutting-cost`, { cutting_cost: v });
      toast.success("Biaya cutting diperbarui — HPP dihitung ulang");
      load();
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingCut(false);
    }
  };
  const saveSewCost = async () => {
    const v = parseFloat(sewCost);
    if (Number.isNaN(v) || v < 0) return toast.error("Ongkos jahit tidak valid");
    setSavingSew(true);
    try {
      await api.put(`/production/orders/${oid}/sewing-cost`, { sewing_cost: v });
      toast.success("Ongkos jahit diperbarui — HPP dihitung ulang");
      load();
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingSew(false);
    }
  };
  const detColor = (detail) => {
    if (!d) return "";
    const productId = detail?.product_id || d.product_id;
    const match = (d.variants || []).find((variant) => (
      variant.product_id === productId
      && variant.size === detail?.size
      && variant.color
    )) || (d.variants || []).find((variant) => (
      variant.product_id === productId && variant.color
    ));
    return match ? match.color : "";
  };
  const canCompleteOrder = (
    d
    && ["Dikirim ke Konveksi", "Sedang Dikerjakan", "Diterima Sebagian"].includes(d.status)
    && Number(d.total_send || 0) > 0
    && Number(d.total_received || 0) >= Number(d.total_send || 0)
  );
  const act = async (path, body, ok) => { try { await api.post(`/production/orders/${oid}/${path}`, body || {}); toast.success(ok); load(); onDone(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const reason = (path, ok) => { const r = window.prompt("Alasan:"); if (r === null) return; act(path, { reason: r }, ok); };
  return (
    <Dialog open={!!oid} onOpenChange={() => setOid(null)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-cocoa">
            {d?.number} — {d?.partner_name}
          </DialogTitle>
          <DialogDescription
            className="sr-only"
            data-testid="order-detail-description"
            id="production-order-detail-description"
          >
            Rincian produk dan penerimaan untuk order jahit ini.
          </DialogDescription>
        </DialogHeader>
        {!d ? <div className="py-10 text-center text-wmuted">Memuat...</div> : (
          <div className="space-y-3">
            <div className="flex gap-3 items-center text-sm"><StatusBadge status={d.status} /><span className="text-wmuted">Kirim {d.total_send} pcs</span><span className="text-wmuted">Est. {fmtIDR(d.cost_estimate)}</span><Button size="sm" variant="outline" onClick={() => openPdf(`/production/orders/${oid}/spk-konveksi.pdf`, `SPK-Konveksi-${d.number}.pdf`)} data-testid="order-spk-btn" className="ml-auto border-cocoa text-cocoa"><Printer className="w-4 h-4 mr-1.5" />Cetak SPK</Button></div>
            <div>
              <div className="text-xs font-semibold text-cocoa uppercase tracking-wide mb-1">Produk Yang Dikirim</div>
              <Panel>
                <div className="divide-y divide-taupe/15 px-1">
                  {(d.details || []).map((x, i) => (
                    <div key={i} className="flex justify-between items-center py-1.5 text-sm" data-testid={`order-send-${i}`}>
                      <span className="text-cocoa font-medium" data-testid={`order-send-product-${i}`}>{x.product_name || d.product_name}{x.color || detColor(x) ? ` ${x.color || detColor(x)}` : ""}{x.size ? ` ${x.size}` : ""}</span>
                      <span className="font-semibold">{x.qty} pcs</span>
                    </div>
                  ))}
                </div>
              </Panel>
            </div>
            {(d.fabrics || []).length > 0 && (
              <div><div className="text-xs font-semibold text-cocoa mb-1 flex items-center justify-between">
                  <span>Asal Cuttingan{d.cutting_number ? <span className="font-mono text-wmuted font-normal"> · {d.cutting_number}</span> : ""}</span>
                  <span className="text-[11px] font-normal text-wmuted" data-testid="order-roll-count">{d.fabrics.length} roll kain</span>
                </div>
                <Panel><Table><TableHeader><TableRow className="hover:bg-transparent"><TableHead className="w-12">Roll</TableHead><TableHead>Kain</TableHead><TableHead>Lot (yard)</TableHead><TableHead className="text-right">Terpakai (yard)</TableHead></TableRow></TableHeader>
                  <TableBody>{d.fabrics.map((f, i) => <TableRow key={i} data-testid={`order-fabric-${i}`}><TableCell className="text-xs font-semibold text-cocoa">#{i + 1}</TableCell><TableCell className="font-medium text-cocoa">{f.name}</TableCell><TableCell className="font-mono text-xs">{f.lot || "-"}</TableCell><TableCell className="text-right">{f.used}</TableCell></TableRow>)}</TableBody>
                </Table></Panel>
              </div>
            )}
            {(d.fabrics || []).length === 0 && (d.fabric_estimate || []).length > 0 && (
              <div><div className="text-xs font-semibold text-cocoa mb-1 flex items-center justify-between">
                  <span>Estimasi Kebutuhan Kain <span className="font-normal text-wmuted">(dari BOM)</span></span>
                  <span className="text-[11px] font-normal text-wmuted" data-testid="order-fabric-est-total">≈ {d.fabric_estimate.reduce((a, x) => a + (x.est_yard || 0), 0).toFixed(2)} yard</span>
                </div>
                <Panel><Table><TableHeader><TableRow className="hover:bg-transparent"><TableHead>Produk</TableHead><TableHead>Warna</TableHead><TableHead>Kain</TableHead><TableHead className="text-right">Pcs</TableHead><TableHead className="text-right">Yard/pcs</TableHead><TableHead className="text-right">Est. Yard</TableHead></TableRow></TableHeader>
                  <TableBody>{d.fabric_estimate.map((f, i) => <TableRow key={i} data-testid={`order-fabric-est-${i}`}><TableCell className="font-medium text-cocoa">{f.product_name}</TableCell><TableCell>{f.color || "-"}</TableCell><TableCell className="text-xs text-wmuted">{f.fabric_name || "-"}</TableCell><TableCell className="text-right">{f.pcs}</TableCell><TableCell className="text-right">{f.yard_per_pcs || "-"}</TableCell><TableCell className="text-right font-semibold">{f.est_yard || 0}</TableCell></TableRow>)}</TableBody>
                </Table></Panel>
                {d.fabric_estimate.some((f) => f.note) && <p className="text-[11px] text-amber-600 mt-1 flex items-start gap-1"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{[...new Set(d.fabric_estimate.filter((f) => f.note).map((f) => f.note))].join(" · ")}</p>}
                <p className="text-[11px] text-wmuted mt-1">Estimasi dari BOM aktif (order legacy tanpa data roll fisik). Roll aktual tampil untuk order via alur Cutting → Order Jahit.</p>
              </div>
            )}
            {d.accessories?.length > 0 && (
              <div><div className="text-xs font-semibold text-cocoa mb-1">Material Aksesori {d.accessories_sent && <span className="text-status-passed font-normal">(terkirim)</span>}</div>
                <Panel><Table><TableHeader><TableRow className="hover:bg-transparent"><TableHead>Aksesori</TableHead><TableHead className="text-right">Qty</TableHead></TableRow></TableHeader>
                  <TableBody>{d.accessories.map((a, i) => <TableRow key={i} data-testid={`order-acc-${i}`}><TableCell>{a.name}</TableCell><TableCell className="text-right">{a.qty} {a.unit}{a.unit === "pack" && a.pack_size ? ` (isi ${a.pack_size} = ${a.qty * a.pack_size} pcs)` : ""}</TableCell></TableRow>)}</TableBody>
                </Table></Panel>
              </div>
            )}
            {accChk && accChk.items.length > 0 && ["Menunggu Approval", "Disetujui", "Siap Dikirim"].includes(d.status) && (
              <div><div className="text-xs font-semibold text-cocoa mb-1">Cek Stok Aksesori (sebelum kirim)</div>
                <Panel><Table><TableHeader><TableRow className="hover:bg-transparent"><TableHead>Aksesori</TableHead><TableHead className="text-right">Butuh</TableHead><TableHead className="text-right">Tersedia</TableHead><TableHead className="text-right">Kurang</TableHead></TableRow></TableHeader>
                  <TableBody>{accChk.items.map((a, i) => <TableRow key={i} data-testid={`acc-check-${i}`}><TableCell>{a.name}</TableCell><TableCell className="text-right">{a.needed}</TableCell><TableCell className="text-right">{a.available}</TableCell><TableCell className={`text-right font-semibold ${a.shortage > 0 ? "text-status-rejected" : "text-status-passed"}`}>{a.shortage > 0 ? a.shortage : "cukup"}</TableCell></TableRow>)}</TableBody>
                </Table></Panel>
                {accChk.has_shortage && <p className="text-[11px] text-status-rejected mt-1 flex items-center gap-1" data-testid="acc-shortage-warn"><AlertTriangle className="w-3.5 h-3.5" />Stok aksesori tidak cukup — lengkapi stok dulu; pengiriman ke konveksi akan ditolak sistem.</p>}
              </div>
            )}
            {d.hpp && (
              <div><div className="text-xs font-semibold text-cocoa mb-1">Rincian HPP Produk</div>
                <Panel><Table>
                  <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Komponen</TableHead><TableHead className="text-right">Per Pcs</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {d.hpp.lines.map((l, i) => <TableRow key={i} data-testid={`order-hpp-${i}`}><TableCell className="text-cocoa">{l.label}</TableCell><TableCell className="text-right">{fmtIDR(l.per_pcs)}</TableCell><TableCell className="text-right">{fmtIDR(l.total)}</TableCell></TableRow>)}
                    <TableRow className="font-semibold bg-ivory/50"><TableCell className="text-cocoa">HPP / pcs (estimasi)</TableCell><TableCell className="text-right text-cocoa" data-testid="order-hpp-perpcs">{fmtIDR(d.hpp.hpp_per_pcs)}</TableCell><TableCell className="text-right text-cocoa">{fmtIDR(d.hpp.total)}</TableCell></TableRow>
                    {d.hpp.passed > 0 && <TableRow className="font-semibold"><TableCell className="text-status-passed">HPP Aktual / pcs (Passed {d.hpp.passed})</TableCell><TableCell className="text-right text-status-passed" data-testid="order-hpp-actual">{fmtIDR(d.hpp.hpp_actual_per_pcs)}</TableCell><TableCell className="text-right text-wmuted">—</TableCell></TableRow>}
                  </TableBody>
                </Table></Panel>
                <p className="text-[11px] text-wmuted mt-1">HPP = Kain + Ongkos Jahit + Aksesori + Ongkos Cutting, dialokasikan untuk {d.hpp.qty} pcs.{d.hpp.passed > 0 ? " Setelah QC diterima, HPP Aktual otomatis menjadi HPP produk di Product List (Produk Jadi)." : " HPP aktual dihitung setelah barang diterima & lolos QC."}</p>
                {write && (
                  <div className="mt-2 flex flex-wrap items-end gap-3 border-t border-taupe/20 pt-2" data-testid="order-cost-editor">
                    <div>
                      <Label className="text-[11px] text-wmuted" htmlFor="order-cutting-cost-input">Ongkos Cutting / Pcs</Label>
                      <Input id="order-cutting-cost-input" data-testid="order-cutting-cost-input" type="number" min="0" value={cutCost} onChange={(e) => setCutCost(e.target.value)} className="w-36 h-8" />
                    </div>
                    <Button size="sm" variant="outline" disabled={savingCut} onClick={saveCutCost} data-testid="order-cutting-cost-save" className="border-cocoa text-cocoa"><Pencil className="w-3.5 h-3.5 mr-1.5" />{savingCut ? "..." : "Simpan"}</Button>
                    <div>
                      <Label className="text-[11px] text-wmuted" htmlFor="order-sewing-cost-input">Ongkos Jahit / Pcs</Label>
                      <Input id="order-sewing-cost-input" data-testid="order-sewing-cost-input" type="number" min="0" value={sewCost} onChange={(e) => setSewCost(e.target.value)} className="w-36 h-8" />
                    </div>
                    <Button size="sm" variant="outline" disabled={savingSew} onClick={saveSewCost} data-testid="order-sewing-cost-save" className="border-cocoa text-cocoa"><Pencil className="w-3.5 h-3.5 mr-1.5" />{savingSew ? "..." : "Simpan"}</Button>
                    <span className="text-[11px] text-wmuted w-full">Untuk order legacy: isi Ongkos Cutting & Ongkos Jahit agar HPP lengkap; HPP dihitung ulang otomatis. Kain & Aksesori terisi otomatis dari BOM.</span>
                  </div>
                )}
              </div>
            )}
            {write && (
              <div className="flex flex-wrap gap-2 pt-2 border-t border-taupe/20 items-center">
                {d.status === "Draft" && <Button size="sm" onClick={() => act("submit", {}, "Diajukan")} data-testid="order-submit-btn" className="bg-cocoa text-ivory"><Send className="w-4 h-4 mr-1.5" />Ajukan</Button>}
                {canApprove && d.status === "Menunggu Approval" && <Button size="sm" onClick={() => act("approve", {}, "Disetujui")} data-testid="order-approve-btn" className="bg-status-passed text-white"><CheckCircle2 className="w-4 h-4 mr-1.5" />Setujui</Button>}
                {d.status === "Siap Dikirim" && <>
                  <Select value={wh} onValueChange={setWh}><SelectTrigger className="w-44" data-testid="order-send-wh"><SelectValue /></SelectTrigger><SelectContent>{sendWhs.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent></Select>
                  <Button size="sm" onClick={() => act("send", { warehouse_id: wh, idempotency_key: `${oid}-${Date.now()}` }, "Dikirim ke konveksi (aksesori dipotong)")} data-testid="order-send-btn" className="bg-cocoa text-ivory"><Truck className="w-4 h-4 mr-1.5" />Kirim ke Konveksi</Button>
                </>}
                {isOwner && d.source === "LEGACY_MFG" && <Button size="sm" variant="outline" onClick={() => setSyncOpen(true)} data-testid="order-legacy-sync-btn" className="border-cocoa text-cocoa"><DatabaseZap className="w-4 h-4 mr-1.5" />Sync Data Legacy</Button>}
                {canCompleteOrder && <Button size="sm" onClick={() => act("complete", {}, "Order ditandai selesai")} data-testid="order-complete-btn" className="bg-status-passed text-white"><CheckCircle2 className="w-4 h-4 mr-1.5" />Selesaikan Order</Button>}
                {!canCompleteOrder && ["Dikirim ke Konveksi", "Sedang Dikerjakan", "Diterima Sebagian"].includes(d.status) && <Button size="sm" onClick={() => setRecv(true)} data-testid="order-receive-btn" className="bg-status-passed text-white"><PackageCheck className="w-4 h-4 mr-1.5" />Terima Produksi & QC</Button>}
                {!canCompleteOrder && ["Dikirim ke Konveksi", "Sedang Dikerjakan", "Diterima Sebagian"].includes(d.status) && <Button size="sm" variant="outline" onClick={() => reason("close-with-variance", "Ditutup dengan selisih")} className="border-taupe text-cocoa" data-testid="order-close-variance-btn">Tutup dgn Selisih</Button>}
                {!["Selesai", "Dibatalkan", "Ditutup dengan Selisih"].includes(d.status) && !d.sent_at && <Button size="sm" variant="outline" onClick={() => reason("cancel", "Dibatalkan")} className="border-status-rejected text-status-rejected ml-auto">Batalkan</Button>}
              </div>
            )}
            <div className="text-xs text-wmuted">Diterima total (semua status): <b>{d.total_received || 0}</b> / {d.total_send} pcs</div>
            {recv && <ReceiveDialog d={d} detColor={detColor} warehouses={recvWhs} onClose={() => setRecv(false)} onDone={() => { setRecv(false); load(); onDone(); }} />}
            {syncOpen && <LegacyMultiProductSyncDialog oid={oid} onClose={() => setSyncOpen(false)} onDone={() => { setSyncOpen(false); load(); onDone(); }} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LegacyMultiProductSyncDialog({ oid, onClose, onDone }) {
  const [raw, setRaw] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const parsePayload = () => {
    try {
      const source = JSON.parse(raw);
      if (!source?.details?.length) {
        throw new Error("JSON harus memiliki daftar details");
      }
      return source;
    } catch (error) {
      toast.error(`JSON tidak valid: ${error.message}`);
      return null;
    }
  };

  const showPreview = async () => {
    const source = parsePayload();
    if (!source) return;
    setBusy(true);
    try {
      const response = await api.post(`/production/orders/${oid}/sync-legacy-multi-product`, {
        ...source,
        confirm: false,
      });
      setPreview(response.data);
      setConfirmed(false);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const applySync = async () => {
    const source = parsePayload();
    if (!source || !preview || !confirmed) return;
    setBusy(true);
    try {
      await api.post(`/production/orders/${oid}/sync-legacy-multi-product`, {
        ...source,
        confirm: true,
        expected_updated_at: preview.current_updated_at,
      });
      toast.success("Data multi-produk legacy berhasil disinkronkan");
      onDone();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="legacy-sync-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-cocoa">Sync Data Legacy Multi-Produk</DialogTitle>
          <DialogDescription className="text-xs text-wmuted" data-testid="legacy-sync-description">
            Paste JSON sumber migrasi. ID produk dan varian dari preview tidak digunakan;
            sistem akan mencocokkan ulang master produk produksi berdasarkan nama, warna, dan ukuran.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>JSON sumber Order Jahit</Label>
            <Textarea
              value={raw}
              onChange={(event) => {
                setRaw(event.target.value);
                setPreview(null);
                setConfirmed(false);
              }}
              placeholder='Paste JSON OJ-L310 di sini'
              className="mt-1 min-h-40 font-mono text-xs"
              data-testid="legacy-sync-json-input"
            />
          </div>
          {preview && (
            <div className="space-y-3" data-testid="legacy-sync-preview">
              <div className="border border-status-passed/30 bg-status-passed/10 rounded-lg p-3 text-sm">
                <div className="font-semibold text-cocoa">
                  {preview.product_names.join(" + ")} · {preview.total_send} kirim · {preview.total_received} diterima
                </div>
                <div className="text-xs text-wmuted mt-1">
                  {preview.migration_validation.mapped_variant_count} varian termapping ·
                  status {preview.migration_validation.status}
                </div>
              </div>
              <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 text-xs text-amber-900">
                Audit stok: stok Available di bawah adalah kondisi fisik saat ini. Sinkronisasi
                ini tidak menambah, mengurangi, atau memindahkan stok fisik.
                {preview.needs_receipt_reconciliation && (
                  <span className="block font-semibold mt-1">
                    Ada perubahan jumlah penerimaan pada record order; tinjau kolom selisih
                    sebelum menyimpan.
                  </span>
                )}
              </div>
              <div className="space-y-2 md:hidden" data-testid="legacy-sync-mobile-stock-list">
                {preview.stock_check.map((row, index) => (
                  <div
                    key={`${row.product_name}-${row.color}-${row.size}`}
                    className="border border-taupe/20 rounded-lg p-3"
                    data-testid={`legacy-sync-mobile-stock-${index}`}
                  >
                    <div className="font-medium text-cocoa">{row.product_name}</div>
                    <div className="text-xs text-wmuted mt-0.5">{row.color} / {row.size}</div>
                    <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                      <div>
                        <div className="text-wmuted">Penerimaan Order</div>
                        <div className="font-semibold text-cocoa">
                          {row.current_recorded_received_qty} → {row.proposed_received_qty}
                        </div>
                      </div>
                      <div>
                        <div className="text-wmuted">Selisih</div>
                        <div className="font-semibold text-cocoa">{row.received_delta}</div>
                      </div>
                      <div>
                        <div className="text-wmuted">Available Saat Ini</div>
                        <div className="font-semibold text-cocoa">
                          {row.current_available_stock}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="hidden overflow-x-auto border border-taupe/20 rounded-lg md:block">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>Produk / Varian</TableHead>
                      <TableHead className="text-right">Penerimaan Order</TableHead>
                      <TableHead className="text-right">Selisih</TableHead>
                      <TableHead className="text-right">Available Saat Ini</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.stock_check.map((row, index) => (
                      <TableRow key={`${row.product_name}-${row.color}-${row.size}`}>
                        <TableCell data-testid={`legacy-sync-stock-${index}`}>
                          <div className="font-medium text-cocoa">{row.product_name}</div>
                          <div className="text-xs text-wmuted">{row.color} / {row.size}</div>
                        </TableCell>
                        <TableCell className="text-right">
                          {row.current_recorded_received_qty} → {row.proposed_received_qty}
                        </TableCell>
                        <TableCell className="text-right">{row.received_delta}</TableCell>
                        <TableCell className="text-right font-semibold text-cocoa">
                          {row.current_available_stock}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <label className="flex items-start gap-2 text-xs text-cocoa cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(event) => setConfirmed(event.target.checked)}
                  data-testid="legacy-sync-confirm-checkbox"
                />
                Saya sudah meninjau pemetaan dan audit stok. Sinkronisasi hanya mengubah record
                Order Jahit, bukan stok fisik.
              </label>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">
            Batal
          </Button>
          {!preview ? (
            <Button onClick={showPreview} disabled={busy} data-testid="legacy-sync-preview-btn" className="bg-cocoa text-ivory">
              {busy ? "Memeriksa..." : "Periksa & Preview"}
            </Button>
          ) : (
            <Button onClick={applySync} disabled={busy || !confirmed} data-testid="legacy-sync-apply-btn" className="bg-status-passed text-white">
              {busy ? "Menyinkronkan..." : "Sinkronkan ke Database"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompanyProfileDialog({ open, setOpen }) {
  const [f, setF] = useState({ company_name: "", address: "", phone: "", logo_url: "" });
  useEffect(() => { if (open) api.get("/production/company-profile").then((r) => setF(r.data)); }, [open]);
  const save = async () => { try { await api.put("/production/company-profile", f); toast.success("Kop SPK disimpan"); setOpen(false); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Kop / Header SPK</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div><Label>Nama Perusahaan</Label><Input value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} data-testid="kop-name" /></div>
          <div><Label>Alamat</Label><Textarea rows={2} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="kop-address" /></div>
          <div><Label>Telepon</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="kop-phone" /></div>
          <div><Label>URL Logo (opsional)</Label><Input value={f.logo_url} onChange={(e) => setF({ ...f, logo_url: e.target.value })} placeholder="https://.../logo.png" data-testid="kop-logo" /><p className="text-[11px] text-wmuted mt-1">Tempel link gambar logo (PNG/JPG). Tampil di kiri atas SPK.</p></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} className="border-taupe text-cocoa">Batal</Button><Button onClick={save} data-testid="kop-save" className="bg-cocoa text-ivory">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
